import { useQuery } from "@tanstack/react-query";
import { useState } from "preact/hooks";

import { api } from "@/api/client";
import type { Paper } from "@/api/types";
import { PaperDetail } from "@/components/paper-card";
import { Dialog, ErrorState, LoadingState, useToast } from "@/components/ui";

export function SearchView() {
  const toast = useToast();
  const [input, setInput] = useState("");
  const [queryText, setQueryText] = useState("");
  const [detail, setDetail] = useState<Paper | null>(null);
  const [added, setAdded] = useState<Set<string>>(() => new Set());

  const results = useQuery({
    queryKey: ["search", queryText],
    queryFn: ({ signal }) => api.search(queryText, signal),
    enabled: queryText.length > 0,
    staleTime: 5 * 60_000,
  });

  const add = async (paper: Paper): Promise<void> => {
    if (!paper.arxiv_id) return;
    try {
      await api.addPaper(paper.arxiv_id);
      setAdded((current) => new Set(current).add(paper.arxiv_id ?? ""));
      toast("已加入收件箱", "success");
    } catch (error) {
      toast(error instanceof Error ? error.message : "加入失败", "error");
    }
  };

  return (
    <main class="search-page">
      <header class="page-heading"><h1>搜索 arXiv</h1><p>输入关键词或 arXiv 原生查询语法。</p></header>
      <form class="search-form" onSubmit={(event) => { event.preventDefault(); setQueryText(input.trim()); }}>
        <label for="paper-search">搜索词</label>
        <div>
          <input id="paper-search" type="search" value={input} onInput={(event) => setInput(event.currentTarget.value)} placeholder="例如 4d radar detection" />
          <button type="submit" class="btn btn-primary" disabled={!input.trim()}>搜索</button>
        </div>
      </form>
      {results.isPending && queryText && <LoadingState label="正在搜索 arXiv…" />}
      {results.isError && <ErrorState error={results.error} retry={() => void results.refetch()} />}
      <section class="paper-list" aria-live="polite">
        {(results.data ?? []).map((paper) => {
          const inLibrary = Boolean((paper as Paper & { in_library?: boolean }).in_library) || (paper.arxiv_id ? added.has(paper.arxiv_id) : false);
          return (
            <article class="card paper-card" key={paper.arxiv_id ?? paper.id}>
              <h2>{paper.title}</h2>
              <p class="authors">{paper.authors.join(", ")}</p>
              <p class="abstract-preview">{paper.abstract}</p>
              <div class="actions">
                <button type="button" class="btn btn-ghost" onClick={() => setDetail(paper)}>详情</button>
                <button type="button" class="btn btn-primary" disabled={inLibrary || !paper.arxiv_id} onClick={() => void add(paper)}>{inLibrary ? "已在库中" : "加入收件箱"}</button>
              </div>
            </article>
          );
        })}
      </section>
      <Dialog title={detail?.title ?? "论文详情"} open={detail !== null} onClose={() => setDetail(null)} wide>
        {detail && <PaperDetail paper={detail} />}
      </Dialog>
    </main>
  );
}
